import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Package, LogIn } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail?.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Panel izquierdo - Imagen */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1622030411594-c282a63aa1bc?crop=entropy&cs=srgb&fm=jpg&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 flex flex-col justify-center p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-sm bg-orange-500 flex items-center justify-center">
              <Package className="w-7 h-7 text-white" />
            </div>
            <span className="text-3xl font-bold text-white tracking-tight">StockFlow</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            Sistema de Control de Inventarios
          </h1>
          <p className="text-lg text-slate-300 max-w-md">
            Gestiona tu inventario, compras y reabasto de manera eficiente con arquitectura empresarial.
          </p>
        </div>
      </div>

      {/* Panel derecho - Formulario */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md shadow-lg border-slate-200">
          <CardHeader className="space-y-1 pb-6">
            <div className="flex items-center gap-2 lg:hidden mb-4">
              <div className="w-10 h-10 rounded-sm bg-blue-900 flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-900">StockFlow</span>
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">Iniciar Sesión</CardTitle>
            <CardDescription className="text-slate-500">
              Ingresa tus credenciales para acceder al sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="animate-fade-in">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Correo electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="usuario@empresa.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                  data-testid="email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11"
                  data-testid="password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-11 bg-blue-900 hover:bg-blue-800 text-white font-medium"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Ingresando...
                  </>
                ) : (
                  'Ingresar'
                )}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-slate-100 rounded-md">
              <p className="text-xs font-medium text-slate-500 mb-2">Credenciales de prueba:</p>
              <div className="space-y-2 text-xs text-slate-600">
                <div className="flex items-center justify-between gap-2">
                  <p><span className="font-medium">Admin:</span> admin@inventory.com / admin123</p>
                  <button
                    type="button"
                    onClick={() => { setEmail('admin@inventory.com'); setPassword('admin123'); }}
                    className="shrink-0 p-1.5 rounded text-slate-500 hover:bg-slate-200 hover:text-blue-900 transition-colors"
                    title="Usar estas credenciales"
                  >
                    <LogIn className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <p><span className="font-medium">Operador:</span> operador@inventory.com / operador123</p>
                  <button
                    type="button"
                    onClick={() => { setEmail('operador@inventory.com'); setPassword('operador123'); }}
                    className="shrink-0 p-1.5 rounded text-slate-500 hover:bg-slate-200 hover:text-blue-900 transition-colors"
                    title="Usar estas credenciales"
                  >
                    <LogIn className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
